import { useCurrentRelease } from "@/common";
import { useGraphVisualisation } from "@/components/GraphVisualisation/hooks/useGraphVisualisation";
import { isFeatureEnabled } from "@/config/features";
import Icon from "@imminently/decisively-ui/Icon";
import NodePath from "@imminently/decisively-ui/NodePath";
import Tooltip from "@imminently/decisively-ui/Tooltip";
import Typography from "@mui/material/Typography";
import get from "lodash/get";
import type React from "react";
import { useTranslation } from "react-i18next";

export interface ResolveErrorInfoProps {
  error: any;
  node?: any;
}

export const useResolveErrorInfo = ({ error, node }: ResolveErrorInfoProps): React.ReactNode => {
  const { t } = useTranslation();
  const release = useCurrentRelease();
  const { graph } = useGraphVisualisation();
  const extendedDebugDiagnosticsEnabled = isFeatureEnabled("experimental.extended_debug_diagnostics");

  if (!error) return null;

  switch (error.type) {
    case "UnknownInstanceCount": {
      const entity = get(error, "data.entity");
      const relationship = entity ? release?.relationships?.find((r) => r.name === entity) : undefined;
      return (
        <span className="flex items-center gap-1">
          The relationship <span className="underline font-semibold">{relationship?.text || "UNKNOWN"}</span> was
          unknown.
        </span>
      );
    }
    case "ResolveLoop": {
      const nodePaths = get(error, "data.paths", []);

      // Find duplicates in the path list to highlight where the loop actually is
      const pathCounts = nodePaths.reduce((acc: Record<string, number>, path: string) => {
        acc[path] = (acc[path] || 0) + 1;
        return acc;
      }, {});
      const duplicates = new Set(Object.keys(pathCounts).filter((path) => pathCounts[path] > 1));

      return (
        <span className="inline-flex items-center gap-1.5">
          <span>There was a circular dependency in the rules, which means this rule cannot be calculated.</span>
          <Tooltip interactable={true}>
            <Tooltip.Trigger asChild={true}>
              <Icon
                name="info"
                className="cursor-help transition-colors text-primary"
                size={20}
              />
            </Tooltip.Trigger>
            <Tooltip.Content className="!max-w-[90vw] !overflow-x-auto">
              <div className="p-2">
                <Typography
                  variant="subtitle2"
                  className="mb-2 font-bold"
                >
                  Dependency Chain:
                </Typography>
                <ol className="m-0 list-decimal pl-8">
                  {nodePaths.map((path: string, index: number) => (
                    <li
                      key={index}
                      className={`text-sm py-0.5 px-1 rounded ${duplicates.has(path) ? "bg-primary-subtle" : ""}`}
                    >
                      <NodePath
                        node={
                          graph.node(path) ?? {
                            path,
                            description: graph.node(path.split("/").pop())?.description,
                          }
                        }
                        showDescription={true}
                      />
                    </li>
                  ))}
                </ol>
              </div>
            </Tooltip.Content>
          </Tooltip>
        </span>
      );
    }
    case "RelationshipResolveFail": {
      if (!extendedDebugDiagnosticsEnabled) {
        return null;
      }

      const relationshipId = get(error, "data.relationshipId");
      const relationship = release?.relationships?.find(
        (r) => r?.guid === relationshipId || r?.id === relationshipId || r?.name === relationshipId,
      );
      const causeType = get(error, "data.cause.type", "UnknownCause");
      const path = get(error, "data.path");

      return (
        <span className="inline-flex flex-col gap-1">
          <span>
            Relationship resolution failed for{" "}
            <span className="underline font-semibold">{relationship?.text || relationshipId || "UNKNOWN"}</span>.
          </span>
          <span className="text-xs text-gray-600">
            Cause: <span className="font-medium">{causeType}</span>
          </span>
          {path ? (
            <span className="text-xs text-gray-600 inline-flex items-center gap-1">
              Path:
              <NodePath
                node={
                  graph.node(path) ?? {
                    path,
                    description: graph.node(path.split("/").pop())?.description,
                  }
                }
                showDescription={true}
              />
            </span>
          ) : null}
        </span>
      );
    }
    default:
      return null;
  }
};

export const ResolveErrorInfo = ({ error, node }: ResolveErrorInfoProps) => {
  return <>{useResolveErrorInfo({ error, node })}</>;
};

export default ResolveErrorInfo;
